<template>
    <label :class="labelClass">
        {{ $t('registerEmail.email') }}:

        <input id="input-email"
            v-model="form.email"
            type="text"
            :disabled="isDisabled"
            :placeholder="$t('registerEmail.enterEmail')"
            :class="['label__input', { 'label__input--active': form.email }]"
            @input="checkEmail"
        >

        <div v-if="error.isVisible"
            class="label__error"
        >
            {{ error.message }}
        </div>
        <span v-if="hint"
            class="label__hint"
        >
            {{ hint }}
        </span>
    </label>
</template>

<script>
// Store
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Locale Modules
import localStoreTypes from '@webapp/store/types.js';

// Helpers
import globalMixin from '@shared/mixins/global-mixin.js';

let timeout = null;

export default {
    name: 'email',
    mixins: [globalMixin],
    props: {
        email: {
            type: String,
            default: '',
        },
        hint: {
            type: String,
            default: '',
        },
        isDisabled: {
            type: Boolean,
            default: false,
        },
        registrationPage: {
            type: Boolean,
            default: false,
        },
        validateOnMount: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isLoading: false,
            form: {
                email: '',
            },
            error: {
                isVisible: false,
                message: '',
            },
            toEmit: {
                invalidEmail: false,
                newEmail: this.email,
            },
        };
    },
    computed: {
        ...mapGetters([
            'emailAvailability',
            'getLatestValidationError',
            'getLatestError',
        ]),

        labelClass() {
            return [
                'label', {
                    'label--half': !this.registrationPage,
                    'label--loading': this.isLoading,
                },
            ];
        },

        newEmailSameAsOriginal() {
            return this.form.email === this.email;
        },
    },
    watch: {
        emailAvailability: 'onEmailAvailability',
        getLatestValidationError: 'onValidationError',
        getLatestError: 'onError',
    },
    async mounted() {
        if (this.validateOnMount) {
            await this.checkEmailAvailability({
                email: this.form.email,
            });
        }

        this.form.email = (this.email) ? this.email : '';
    },
    methods: {
        ...mapActions({
            setIsEmailValid: localStoreTypes.SET_IS_EMAIL_VALID,
            setRegistration: localStoreTypes.SET_REGISTRATION,
        }),

        ...mapActions(['checkEmailAvailability']),

        /**
         * Checking email
         *
         * @returns {void}
         */
        checkEmail() {
            this.toEmit.newEmail = this.form.email;
            this.toEmit.invalidEmail = false;
            this.error.message = '';
            this.error.isVisible = false;

            if (this.newEmailSameAsOriginal) {
                return this.$emit('toggleEmail', this.toEmit);
            }

            if (timeout) {
                clearTimeout(timeout);
            }

            // eslint-disable-next-line max-len
            const regex = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

            // Call debounce method when email is valid
            if (regex.test(this.form.email)) {
                timeout = setTimeout(async() => {
                    this.isLoading = true;

                    await this.checkEmailAvailability({
                        email: this.form.email,
                    });
                }, 300);
            } else {
                this.error.isVisible = true;
                this.error.message = this.$t('registerEmail.invalidEmail');
                this.toEmit.invalidEmail = true;
                this.setIsEmailValid(false);

                this.$emit('toggleEmail', this.toEmit);
            }
        },

        /**
         * Checking email availability api response
         *
         * @param {object} newValue The object received from api to check of email available or not
         * @returns {void}
         */
        onEmailAvailability(newValue) {
            if (Object.keys(newValue).length > 0) {
                this.error.isVisible = !newValue.isValid || !newValue.isAvailable;
                this.isLoading = false;

                if (!newValue.isValid) {
                    this.error.message = this.$t('registerEmail.invalidEmail');
                    this.toEmit.invalidEmail = true;
                }

                if (!newValue.isAvailable && !this.newEmailSameAsOriginal) {
                    this.error.message = this.$t('registerEmail.emailAlreadyInUse');
                    this.toEmit.invalidEmail = true;
                }

                this.$emit('toggleEmail', this.toEmit);
                this.setIsEmailValid(!this.error.isVisible);
                this.setRegistration([
                    {
                        property: 'email',
                        value: this.form.email,
                    },
                ]);
            }
        },

        /**
         * On api validation error
         *
         * @param {object} error The object received from api
         * @param {string} error.email Email
         * @param {string} error.httpException Exception message
         * @returns {void}
         */
        onValidationError({
            email = '',
            httpException = '',
        }) {
            this.$notify({
                type: 'error',
                text: email || httpException,
            });

            this.isLoading = false;
        },

        /**
         * On api error
         *
         * @param {object} error The object received from api
         * @returns {void}
         */
        onError(error) {
            const fetchErrors = this.utilGlobalHandleErrorMessages(error);

            this.$notify({
                type: 'error',
                text: fetchErrors.message,
            });
        },
    },
};
</script>